import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import { useId } from "react";

interface ConfigurationOptionProps {
    id?: string
    label: string
    //highlightLabel emphasises the part of label that matches it, for labels
    //that name a link or a product term
    highlightLabel?: string
    description?: string
    checked?: boolean
    onChange?: (b: boolean) => void
    hideCheckbox?: boolean
    disabled?: boolean
    children?: React.ReactNode
}

//escapeRegExp makes a highlight literal. The value is interpolated into a
//RegExp, so a metacharacter in it would otherwise change the split ("(docs)"
//loses the highlight, "[beta]" shreds the label) or throw outright ("C++").
function escapeRegExp(v: string) {
    return v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

//renderLabel splits the label around highlightLabel so the matching run can be
//emphasised. Without a highlight it is just the label.
function renderLabel(label: string, highlight: string | undefined, linkColor: string) {
    if (!highlight) {
        return label;
    }

    return label.split(new RegExp(`(${escapeRegExp(highlight)})`, "i")).map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase()
            ? <strong key={index} style={{ color: linkColor, fontStyle: "italic" }}>{part}</strong>
            : part
    );
}

//ConfigurationOption is one labelled setting on a configuration page: an
//optional checkbox, a heading, a description and anything the caller nests
//underneath.
export function ConfigurationOption(props: ConfigurationOptionProps) {
    //the visible heading is the checkbox's label, so tie the two together:
    //the checkbox is rendered as a sibling and would otherwise have no
    //accessible name at all
    const generatedId = useId();
    const labelId = `${props.id || generatedId}-label`;

    const theme = useTheme();

    return (
        <Box display="flex" sx={{ alignItems: "start", marginBottom: "10px" }}>
            {!props.hideCheckbox &&
                <Checkbox
                    id={props.id}
                    disabled={props.disabled}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: "36px" } }}
                    checked={props.checked}
                    onChange={(x) => props.onChange && props.onChange(x.target.checked)}
                    inputProps={{ "aria-labelledby": labelId }}
                />
            }
            <Box sx={{ marginLeft: "10px", marginTop: "6px", width: "100%" }}>
                <Typography id={labelId} variant="h4" color={props.disabled ? "text.disabled" : undefined}>
                    {renderLabel(props.label, props.highlightLabel, theme.palette.custom.link)}
                </Typography>
                <Typography variant="body2" color={props.disabled ? "text.disabled" : "text.secondary"}>
                    {props.description}
                </Typography>
                {props.children}
            </Box>
        </Box>
    );
}

export default ConfigurationOption;
